import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Moon, Flame } from "lucide-react";

/**
 * BLOOD MOON banner — visible only when the weekly Friday-UTC window is live.
 * Polls /api/attacks/blood_moon on mount + every 5 minutes. Stays out of
 * the DOM entirely when inactive so it can't affect quiet weekdays.
 */
export default function BloodMoonBanner() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/attacks/blood_moon");
        if (!cancelled) setData(data);
      } catch (e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data) return null;

  // Blood Moon is LIVE now — full banner
  if (data.active) {
    const ends = new Date(data.ends_at);
    const now = new Date();
    const hoursLeft = Math.max(0, Math.round((ends - now) / 3_600_000));

    return (
      <div className="border border-blood bg-[#1a0606] px-4 py-3 mb-6 flex items-start gap-3 shadow-[0_0_24px_rgba(106,0,0,0.45)]"
           data-testid="blood-moon-banner">
        <Moon className="w-5 h-5 text-blood mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[0.65rem] tracking-[0.25em] text-blood flex items-center gap-2">
            <Flame className="w-3 h-3" />
            BLOOD MOON · ACTIVE
          </p>
          <p className="text-bone text-sm mt-1 leading-snug">
            Defender shields are halved (<span className="text-blood">12h</span>).
            Revenge stake transfers triple (<span className="text-blood">×3</span>).
            The world runs hot — if you have a vendetta, ride it tonight.
          </p>
        </div>
        <p className="font-mono text-[0.65rem] text-muted-h tracking-widest text-right flex-shrink-0"
           data-testid="blood-moon-countdown">
          {hoursLeft}h LEFT
        </p>
      </div>
    );
  }

  // Blood Moon RISES within 24h — subtle teaser strip
  const starts = new Date(data.next_starts_at);
  const nowD = new Date();
  const hoursUntil = Math.round((starts - nowD) / 3_600_000);
  if (hoursUntil > 0 && hoursUntil <= 24) {
    return (
      <div className="border border-blood/40 bg-black px-4 py-2 mb-6 flex items-center gap-3"
           data-testid="blood-moon-teaser">
        <Moon className="w-3.5 h-3.5 text-blood/70 flex-shrink-0" strokeWidth={1.5} />
        <p className="font-mono text-[0.65rem] tracking-[0.25em] text-blood/80 flex-1">
          BLOOD MOON RISES IN {hoursUntil}H
        </p>
        <p className="font-mono text-[0.55rem] text-muted-h tracking-widest text-right flex-shrink-0">
          SHIELDS -50% · REVENGE ×3
        </p>
      </div>
    );
  }

  return null;
}
